import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown, CalendarCheck, MapPinned, MessagesSquare, Smartphone } from "lucide-react";
import { useLang } from "@/i18n";
import { audienceImages, destinations, experienceImages, photos, rooms } from "@/data/site";
import { Marquee } from "@/components/site/Marquee";
import { Reveal } from "@/components/site/Reveal";
import { Section } from "@/components/site/Section";
import { Comfort } from "@/components/site/Comfort";
import { CtaBanner } from "@/components/site/CtaBanner";
import { ReviewsSection } from "@/components/site/ReviewsSection";
import { SEO } from "@/components/site/SEO";

const DIRECT_ICONS = { MessagesSquare, Smartphone, MapPinned, CalendarCheck };

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} data-testid="hero" className="relative flex min-h-[100svh] items-center overflow-hidden bg-mare">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={photos.hero} alt="Lungomare di Marina di Ragusa al tramonto" className="h-full w-full object-cover" fetchPriority="high" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-ink/75 via-ink/40 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent" />

      <div className="relative mx-auto w-full max-w-7xl px-4 pb-28 pt-32 sm:px-6 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.7 }}
          className="mb-5 text-xs font-bold uppercase tracking-[0.3em] text-ochre sm:text-sm"
        >
          {t.hero.eyebrow}
        </motion.p>
        <h1 className="max-w-3xl font-serif text-4xl font-semibold leading-[1.05] text-sand text-balance sm:text-6xl lg:text-7xl">
          {t.hero.lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.45 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-sand/90 sm:text-lg"
        >
          {t.hero.sub}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.7 }}
          className="mt-8 flex flex-wrap gap-3"
        >
          <Link
            to="/la-casa"
            data-testid="hero-cta-discover"
            className="inline-flex items-center gap-2 rounded-full bg-sand px-7 py-3.5 text-sm font-bold text-mare transition-all duration-200 hover:-translate-y-0.5 hover:shadow-2xl"
          >
            {t.hero.cta1} <ArrowRight size={16} />
          </Link>
          <Link
            to="/prenota"
            data-testid="hero-cta-availability"
            className="inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3.5 text-sm font-bold text-sand shadow-xl shadow-terracotta/30 transition-all duration-200 hover:bg-terracotta-dark hover:-translate-y-0.5"
          >
            {t.hero.cta2}
          </Link>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-2"
        >
          {t.hero.badges.map((b) => (
            <span key={b} className="rounded-full border border-sand/25 bg-ink/25 px-3.5 py-1.5 text-xs font-semibold text-sand backdrop-blur-sm">
              {b}
            </span>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.9 }}
        className="absolute bottom-20 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-1 text-sand/70 md:flex"
      >
        <span className="text-[10px] font-bold uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ChevronDown size={18} />
        </motion.span>
      </motion.div>
    </section>
  );
};

const Welcome = () => {
  const { t } = useLang();
  return (
    <section className="py-16 sm:py-24 lg:py-28" data-testid="welcome-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <div>
          <Reveal>
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.28em] text-terracotta">{t.welcome.eyebrow}</p>
            <h2 className="font-serif text-3xl font-semibold leading-tight tracking-tight text-ink text-balance sm:text-4xl lg:text-5xl">
              {t.welcome.title}
            </h2>
            <p className="mt-6 text-base leading-relaxed text-ink-muted sm:text-lg">{t.welcome.p1}</p>
            <p className="mt-4 text-base leading-relaxed text-ink-muted">{t.welcome.p2}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 grid grid-cols-4 gap-3">
              {t.welcome.stats.map((s) => (
                <div key={s.l} className="rounded-2xl border border-border bg-white p-4 text-center shadow-sm">
                  <div className="font-serif text-2xl font-semibold text-terracotta sm:text-3xl">{s.v}</div>
                  <div className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">{s.l}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
        <Reveal>
          <img src={photos.cucinaWide} alt="La cucina di Casa Vacanze Margherita con il tavolo apparecchiato e il balcone" loading="lazy" className="h-[420px] w-full rounded-3xl object-cover shadow-2xl sm:h-[540px]" data-testid="welcome-kitchen-img" />
        </Reveal>
      </div>
    </section>
  );
};

const SeaCulture = () => {
  const { t } = useLang();
  const Panel = ({ data, img, alt, testid }) => (
    <div data-testid={testid} className="group relative min-h-[46vh] flex-1 overflow-hidden transition-all duration-700 ease-out md:hover:flex-[1.5]">
      <img src={img} alt={alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-transparent" />
      <div className="relative flex h-full flex-col justify-end p-7 sm:p-10">
        <span className="font-script text-3xl text-ochre sm:text-4xl">{data.label}</span>
        <h3 className="mt-2 font-serif text-2xl font-semibold text-sand sm:text-3xl">{data.title}</h3>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-sand/85">{data.text}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {data.points.map((p) => (
            <span key={p} className="rounded-full border border-sand/30 bg-ink/25 px-3 py-1 text-xs font-semibold text-sand backdrop-blur-sm">{p}</span>
          ))}
        </div>
      </div>
    </div>
  );
  return (
    <section className="py-16 sm:py-24" data-testid="sea-culture-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Section eyebrow={t.seaCulture.eyebrow} title={t.seaCulture.title} testid="sea-culture-heading" center />
        <Reveal>
          <p className="mx-auto -mt-4 mb-12 max-w-2xl text-center font-script text-2xl leading-snug text-mare sm:text-3xl">
            “{t.seaCulture.motto}”
          </p>
        </Reveal>
        <div className="flex flex-col gap-4 overflow-hidden rounded-3xl md:h-[72vh] md:flex-row">
          <Panel data={t.seaCulture.sea} img={photos.spiaggiaTramonto} alt="Spiaggia al tramonto sulla costa ragusana" testid="sea-panel" />
          <Panel data={t.seaCulture.culture} img={photos.modica} alt="Il Duomo di San Giorgio a Modica illuminato di notte" testid="culture-panel" />
        </div>
        <Reveal className="mt-8 text-center">
          <Link to="/cosa-vedere" data-testid="sea-culture-cta" className="inline-flex items-center gap-2 rounded-full border-2 border-mare px-7 py-3 text-sm font-bold text-mare transition-all duration-200 hover:bg-mare hover:text-sand">
            {t.seaCulture.cta} <ArrowRight size={16} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const RoomsTeaser = () => {
  const { t } = useLang();
  return (
    <section className="bg-sand-deep py-16 sm:py-24" data-testid="rooms-teaser">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Section eyebrow={t.roomsTeaser.eyebrow} title={t.roomsTeaser.title} intro={t.roomsTeaser.text} testid="rooms-teaser-heading" />
        <div className="grid gap-6 md:grid-cols-3">
          {rooms.map((room, i) => {
            const r = t.rooms[room.id];
            return (
              <Reveal key={room.id} delay={i * 0.1}>
                <Link to="/camere" data-testid={`room-teaser-${room.id}`} className="group block overflow-hidden rounded-2xl bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                  <div className="h-60 overflow-hidden">
                    <img src={room.img} alt={r.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-xl font-semibold text-ink">{r.name}</h3>
                    <p className="mt-1 text-sm font-semibold text-terracotta">{r.beds}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-mare">
                      {t.roomsTeaser.cta} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Position = () => {
  const { t } = useLang();
  return (
    <section className="py-16 sm:py-24" data-testid="position-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <Reveal>
          <div className="relative">
            <img src={photos.ragusaIbla} alt="Ragusa Ibla al tramonto vista dai vicoli" loading="lazy" className="h-[420px] w-full rounded-3xl object-cover shadow-2xl sm:h-[520px]" />
            <img src={photos.puntaSecca} alt="La spiaggia di Punta Secca" loading="lazy" className="absolute -bottom-6 -right-3 hidden w-44 rotate-3 rounded-2xl border-4 border-sand object-cover shadow-xl sm:block" />
          </div>
        </Reveal>
        <div>
          <Section eyebrow={t.position.eyebrow} title={t.position.title} intro={t.position.text} testid="position-heading" />
          <div className="grid grid-cols-2 gap-2.5">
            {destinations.map((d) => (
              <div key={d.id} className="flex items-center justify-between rounded-xl border border-border bg-white px-4 py-3 shadow-sm" data-testid={`distance-chip-${d.id}`}>
                <span className="text-sm font-semibold text-ink">{t.destinations[d.id].name}</span>
                <span className={`ml-2 shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${d.time === "0" ? "bg-terracotta text-sand" : "bg-sand-deep text-mare"}`}>
                  {d.time === "0" ? t.position.here : `${d.time} ${t.position.min}`}
                </span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-ink-muted">{t.position.note}</p>
          <Link to="/dove-siamo" data-testid="position-cta" className="mt-6 inline-flex items-center gap-2 rounded-full bg-mare px-6 py-3 text-sm font-bold text-sand transition-all duration-200 hover:bg-mare-light hover:-translate-y-0.5">
            {t.position.cta} <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

const Experiences = () => {
  const { t } = useLang();
  return (
    <section className="bg-mare py-16 text-sand sm:py-24 grain relative" data-testid="experiences-section">
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 max-w-3xl sm:mb-14">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.28em] text-ochre">{t.experiences.eyebrow}</p>
          <h2 className="font-serif text-3xl font-semibold leading-tight text-balance sm:text-4xl lg:text-5xl">{t.experiences.title}</h2>
          <p className="mt-4 text-base leading-relaxed text-sand/75 sm:text-lg">{t.experiences.text}</p>
        </div>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
          {t.experiences.items.slice(0, 6).map((exp, i) => (
            <Reveal key={exp.id} delay={i * 0.05}>
              <div data-testid={`experience-card-${exp.id}`} className="group relative h-52 overflow-hidden rounded-2xl sm:h-64">
                <img src={experienceImages[exp.id]} alt={exp.t} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
                  <h3 className="font-serif text-lg font-semibold sm:text-xl">{exp.t}</h3>
                  <p className="mt-1 hidden text-xs leading-relaxed text-sand/80 sm:block">{exp.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10 text-center">
          <Link to="/esperienze" data-testid="experiences-more-link" className="inline-flex items-center gap-2 rounded-full border border-sand/30 px-6 py-3 text-sm font-bold text-sand transition-all duration-200 hover:bg-sand hover:text-mare">
            {t.experiences.more} <ArrowRight size={16} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const Audience = () => {
  const { t } = useLang();
  return (
    <section className="py-16 sm:py-24" data-testid="audience-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Section eyebrow={t.audience.eyebrow} title={t.audience.title} testid="audience-heading" />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {t.audience.items.map((a, i) => (
            <Reveal key={a.id} delay={i * 0.08}>
              <div data-testid={`audience-card-${a.id}`} className="group overflow-hidden rounded-2xl border border-border bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                <div className="h-44 overflow-hidden">
                  <img src={audienceImages[a.id]} alt={a.t} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <h3 className="font-serif text-lg font-semibold text-ink">{a.t}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink-muted">{a.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Direct = () => {
  const { t } = useLang();
  return (
    <section className="py-14 sm:py-20" data-testid="direct-booking-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-3xl border border-ochre/30 bg-gradient-to-br from-sand-deep to-stone-warm/60 p-6 sm:p-9">
          <Section eyebrow={t.direct.eyebrow} title={t.direct.title} testid="direct-heading" />
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {t.direct.items.map((item, i) => {
              const Icon = DIRECT_ICONS[item.icon];
              return (
                <Reveal key={item.t} delay={i * 0.08}>
                  <div data-testid={`direct-benefit-${i}`}>
                    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-white text-terracotta shadow-sm">
                      <Icon size={22} />
                    </div>
                    <h3 className="font-serif text-lg font-semibold text-ink">{item.t}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-ink-muted">{item.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <Reveal className="mt-10">
            <Link to="/prenota" data-testid="direct-cta" className="inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3.5 text-sm font-bold text-sand shadow-lg shadow-terracotta/25 transition-all duration-200 hover:bg-terracotta-dark hover:-translate-y-0.5">
              {t.direct.cta} <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <SEO
        title="Casa Vacanze Margherita | From Sea to Culture — Santa Croce Camerina, Sicilia"
        description="Casa vacanze a Santa Croce Camerina (Ragusa) con 3 camere, fino a 6 ospiti, a pochi minuti da Marina di Ragusa e Punta Secca. From Sea to Culture."
        path="/"
      />
      <Hero />
      <Marquee />
      <Welcome />
      <Comfort />
      <SeaCulture />
      <RoomsTeaser />
      <Position />
      <Experiences />
      <Audience />
      <ReviewsSection />
      <Direct />
      <CtaBanner />
    </>
  );
}
